/**
 * useCacheCleanup.ts
 * Hook React pour remplacer les setInterval de nettoyage de cache
 * Utilise useEffect + refs pour gérer le nettoyage périodique
 */
import { useEffect, useRef } from 'react';
import { KonvaCacheManager } from '../utils/KonvaCacheManager';
import { cleanupExpiredCache } from '../utils/signedUrlCache';
import { logger } from '../utils/ProfessionalLogger';

/**
 * Hook pour nettoyer le cache Konva périodiquement
 * Remplace le setInterval dans KonvaCacheManager
 */
export function useKonvaCacheCleanup(enabled: boolean = true): void {
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const cacheManagerRef = useRef<KonvaCacheManager | null>(null);

  useEffect(() => {
    if (!enabled) return;

    const cacheManager = KonvaCacheManager.getInstance();
    cacheManagerRef.current = cacheManager;

    const scheduleCleanup = () => {
      // Nettoyer le timeout précédent
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      // Programmer le prochain nettoyage
      timeoutRef.current = setTimeout(() => {
        try {
          cacheManager.performCleanup();
          // Réprogrammer pour la prochaine fois
          scheduleCleanup();
        } catch (error) {
          logger.error('Erreur lors du nettoyage du cache Konva:', error);
          // Réessayer après un délai en cas d'erreur
          scheduleCleanup();
        }
      }, cacheManager.getConfig().cacheTimeout);
    };

    // Démarrer le cycle de nettoyage
    scheduleCleanup();

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, [enabled]);
}

/**
 * Hook pour nettoyer le cache des signed URLs périodiquement
 * Remplace le setInterval dans signedUrlCache.ts
 */
export function useSignedUrlCacheCleanup(enabled: boolean = true): void {
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (!enabled) return;

    const CLEANUP_INTERVAL = 10 * 60 * 1000; // 10 minutes

    const scheduleCleanup = () => {
      // Nettoyer le timeout précédent
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      // Programmer le prochain nettoyage
      timeoutRef.current = setTimeout(() => {
        try {
          cleanupExpiredCache();
          // Réprogrammer pour la prochaine fois
          scheduleCleanup();
        } catch (error) {
          logger.error('Erreur lors du nettoyage du cache signed URLs:', error);
          // Réessayer après un délai en cas d'erreur
          scheduleCleanup();
        }
      }, CLEANUP_INTERVAL);
    };

    // Démarrer le cycle de nettoyage
    scheduleCleanup();

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, [enabled]);
}
